import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { Search, ArrowRight, CheckCircle, Phone, Clock, FileText, DollarSign, Shield } from 'lucide-react';

const whatWeReview = [
  'Business income and expense classifications',
  'Missed deductions and credits',
  'Depreciation methods and Section 179 elections',
  'Vehicle and mileage deductions',
  'Home office deduction eligibility',
  'Retirement contribution deductions',
  'Self-employment tax calculations',
  'Entity structure optimization (LLC, S-Corp, etc.)',
  'Estimated tax payment accuracy',
  'Prior-year carryforward items',
];

const whoQualifies = [
  'Self-employed individuals and sole proprietors',
  'LLC and S-Corp owners',
  'Independent contractors and gig workers',
  'Small business owners with W-2 employees',
  'Anyone who filed a business return in the last 3 years',
  'Business owners who changed tax preparers',
  'Owners who feel they paid too much in taxes',
];

const steps = [
  { number: '1', title: 'Send Us Your Return', description: 'Provide a copy of your previously filed business tax return. We accept returns filed within the last 3 years.' },
  { number: '2', title: 'We Review It — Free', description: 'Our team examines your return for missed deductions, credits, and errors that could lower your tax bill or increase your refund.' },
  { number: '3', title: 'Get Your Results', description: 'We tell you upfront if you may qualify for a refund or a lower tax bill. If an amendment makes sense, we explain the next steps and costs.' },
];

export default function SecondLookReview() {
  useSEO({
    title: 'Free Second Look Tax Review',
    description: 'Already filed your business tax return? Our free Second Look Review checks for missed deductions and credits. The IRS allows up to 3 years to amend. Find out if you qualify for a refund.',
  });

  return (
    <div data-testid="second-look-review-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
              Second Look Review — Free
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
              Already Filed? We'll Take a Second Look — For Free.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-4">
              The IRS generally allows up to 3 years to amend a return. Our Second Look Review is free. We will review your previously filed business tax return and let you know upfront if you may qualify for a refund or a lower tax bill.
            </p>
            <p className="text-base text-slate-400 mb-8">
              No cost. No obligation. Just a professional review of what was already filed.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/ask">
                <Button
                  data-testid="second-look-hero-cta"
                  className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md"
                >
                  Request Your Free Review
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:8555892461">
                <Button
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-semibold px-7 py-6 text-base rounded-md"
                >
                  <Phone className="w-4 h-4 mr-2" /> 855-589-2461
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              How It Works
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-14 max-w-lg">
              Three simple steps to find out if you're owed money
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.12}>
                <div className="relative">
                  <span className="font-heading font-bold text-6xl text-brand-navy/5 absolute -top-4 -left-2">
                    0{step.number}
                  </span>
                  <div className="relative pt-8">
                    <div className="w-12 h-12 rounded-lg bg-brand-navy flex items-center justify-center mb-5">
                      <span className="text-brand-green font-heading font-bold text-lg">{step.number}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-brand-navy mb-3">
                      {step.title}
                    </h3>
                    <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222]">
                      {step.description}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* What We Review */}
      <section className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <AnimatedSection>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
                What We Look For
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-6">
                A thorough review of your filed return
              </h2>
              <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] mb-8">
                We examine your business tax return line by line, looking for missed opportunities that could put money back in your pocket.
              </p>
              <div className="space-y-3">
                {whatWeReview.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-brand-green mt-1 flex-shrink-0" />
                    <span className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222]">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <div className="bg-brand-navy rounded-lg p-8 text-white">
                <DollarSign className="w-10 h-10 text-brand-green mb-5" />
                <h3 className="font-heading font-bold text-xl mb-4">Why This Matters</h3>
                <div className="space-y-4">
                  <p className="text-sm text-slate-300 leading-relaxed">
                    Tax law is complex, and even experienced preparers can miss deductions or make classification errors. A second set of eyes — especially one focused on business returns — can uncover savings that were left on the table.
                  </p>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    The IRS allows amended returns for up to 3 years from the original filing date. If we find something, there's still time to act.
                  </p>
                  <div className="mt-6 p-4 bg-white/10 rounded-lg">
                    <p className="text-sm text-white font-medium">Common findings include:</p>
                    <ul className="mt-3 space-y-2">
                      <li className="flex items-center gap-2 text-sm text-slate-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-green flex-shrink-0" />
                        Missed vehicle and mileage deductions
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-green flex-shrink-0" />
                        Incorrect depreciation methods
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-green flex-shrink-0" />
                        Unclaimed home office deduction
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-green flex-shrink-0" />
                        Overlooked credits and carryforwards
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Who Qualifies */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              Who Qualifies
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-6">
              Is the Second Look Review right for you?
            </h2>
            <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] mb-8">
              If you filed a business tax return in the last 3 years, you may qualify for our free review:
            </p>
            <div className="space-y-3">
              {whoQualifies.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-brand-green mt-1 flex-shrink-0" />
                  <span className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222]">{item}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <Shield className="w-12 h-12 text-brand-green mx-auto mb-5" />
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-white mb-4">
              It costs nothing to find out
            </h2>
            <p className="text-base text-slate-400 mb-8 max-w-lg mx-auto">
              Our Second Look Review is completely free. If we find potential savings, we'll explain your options and the cost of amending before you commit to anything.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/ask">
                <Button className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md">
                  Request Your Free Review
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:8555892461">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold px-7 py-6 text-base rounded-md">
                  <Phone className="w-4 h-4 mr-2" /> 855-589-2461
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
